import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ApiServices from "../Services/ApiServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faGraduationCap,
  faUserGraduate,
  faArrowLeft,
  faBook,
  faAward,
  faCamera,
  faEnvelope,
  faHeart,
  faPhone,
  faShare,
  faChalkboardTeacher,
  faUser,
  faUsers,
  faVenusMars,
  faClipboardCheck,
  faCertificate,
  faLightbulb,
  faQuoteRight,
  faMapPin,
  faLayerGroup,
  faCheckCircle
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebookF,
  faTwitter,
  faLinkedinIn,
  faWhatsapp
} from "@fortawesome/free-brands-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { getFullFileUrl } from "../../Helpers/UrlHelpers";
import Loader from "../Shared/Loader";
import { toastHelper } from "../../Helpers/toastHelper";
import Header from "../Shared/Header";
import Footer from "../Shared/Footer";
import DynamicModal from "../Shared/DynamicModal";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const TeacherProfile = () => {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [showRegisterForm, setShowRegisterForm] = useState(false);

  useEffect(() => {
    const fetchTeacher = async () => {
      try {
        setLoading(true);
        const response = await ApiServices.get(`Teachers/${teacherId}`);
        setTeacher(response.data);
      } catch (error) {
        toastHelper.error("تعذر تحميل بيانات المعلم");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    fetchTeacher();
  }, [teacherId, navigate]);

  const handleRateTeacher = async (rating) => {
    try {
      await ApiServices.put("Teacher/Rate", { teacher_id: parseInt(teacherId), rate: rating });
      setTeacher((prev) => ({ ...prev, rate: rating }));
      toastHelper.success("تم تقييم المعلم بنجاح");
    } catch (error) { toastHelper.error("يجب تسجيل الدخول للتقييم"); }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = `${teacher?.full_name} - معلم متميز`;
    let shareUrl = "";
    switch (platform) {
      case "facebook": shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`; break;
      case "twitter": shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`; break;
      case "whatsapp": shareUrl = `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`; break;
      default: navigator.clipboard.writeText(url).then(() => { toastHelper.success("تم نسخ الرابط"); setShowShareMenu(false); }); return;
    }
    window.open(shareUrl, "_blank", "width=600,height=400");
    setShowShareMenu(false);
  };

  if (loading) return <div className="tp-root"><Header /><div className="tp-loader-box"><Loader /></div></div>;
  if (!teacher) return null;

  const registerFormConfig = (teacherIdFromProp) => [
    { name: 'fullName', label: 'الاسم بالكامل', type: 'text', icon: faUser, required: true },
    { name: 'email', label: 'البريد الإلكتروني ولي الأمر', type: 'email', icon: faEnvelope, required: true },
    { name: 'phone', label: 'رقم الهاتف ولي الأمر', type: 'tel', icon: faPhone, required: true, pattern: /^[0-9]{11}$/ },
    { name: 'gender', label: 'الجنس', type: 'select', icon: faVenusMars, required: true, options: [{ value: '1', label: 'ذكر' }, { value: '2', label: 'أنثى' }] },
    { name: 'user_class', label: 'الصف', type: 'async-select', icon: faGraduationCap, required: true, optionsUrl: 'lookup/userClasses', optionsMap: (item) => ({ value: item.id, label: item.name }) },
    { name: 'material_id', label: 'المادة', type: 'async-select', icon: faBook, required: true, optionsUrl: 'lookup/materials', optionsMap: (item) => ({ value: item.id, label: item.name }) },
    {
      name: 'teacher_id', label: 'المعلم', type: 'async-select', icon: faChalkboardTeacher, required: true,
      optionsUrl: 'lookup/teachers', optionsMap: (t) => ({ value: String(t.id), label: t.name ?? t.full_name }),
      ...(teacherIdFromProp ? { lockedValue: String(teacherIdFromProp), lockedLabel: teacher.full_name } : {}),
    },
    { name: 'profile_picture', label: 'الصورة الشخصية (اختياري)', type: 'file', icon: faCamera, accept: 'image/*' },
  ];

  const stats = [
    { icon: faUsers, value: teacher.student_count || 0, label: "طالب نشط", color: "#6366f1" },
    { icon: faClipboardCheck, value: teacher.exams_count || 0, label: "اختبار متاح", color: "#6366f1" },
    { icon: faBook, value: teacher.blog_count || 0, label: "مقال تعليمي", color: "#10b981" },
    { icon: faAward, value: teacher.teaching_hours || "100+", label: "ساعة تدريس", color: "#8b5cf6" }
  ];

  return (
    <div className="tp-root" dir="rtl">
      <Header />

      <section className="tp-content">
        <div className="tp-container">
          <div className="tp-grid">

            {/* Left Column: Fixed Profile Card */}
            <aside className="tp-aside">
              <motion.div
                className="tp-profile-card"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
              >
                <div className="tp-avatar-box">
                  <div className="tp-avatar-ring" />
                  <img src={getFullFileUrl(teacher.image)} alt={teacher.full_name} className="tp-avatar-img" />
                  <div className="tp-verified-badge"><FontAwesomeIcon icon={faCheckCircle} /></div>
                </div>

                <div className="tp-profile-header">
                  <h1 className="tp-teacher-name">{teacher.full_name}</h1>
                  <p className="tp-qualification">
                    <FontAwesomeIcon icon={faGraduationCap} />
                    {teacher.qualification}
                  </p>
                </div>

                <div className="tp-rating-box">
                  <div className="tp-rating-head">
                    <div className="tp-rating-val">
                      <span className="num">{teacher.rate}</span>
                      <span className="max">/5</span>
                    </div>
                    <span className="lbl">تقييم الطلاب</span>
                  </div>
                  <div className="tp-stars">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button
                        key={s} className={`tp-star-btn ${s <= (hoveredStar || teacher.rate) ? 'is-active' : ''}`}
                        onMouseEnter={() => setHoveredStar(s)} onMouseLeave={() => setHoveredStar(0)}
                        onClick={() => handleRateTeacher(s)}
                      >
                        <FontAwesomeIcon icon={faStar} />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="tp-quick-meta">
                  <div className="tp-meta-pill">
                    <FontAwesomeIcon icon={faBook} />
                    <span>{teacher.specialization || "معلم معتمد"}</span>
                  </div>
                  <div className="tp-meta-pill">
                    <FontAwesomeIcon icon={faChalkboardTeacher} />
                    <span>{teacher.material || "جميع المواد"}</span>
                  </div>
                </div>

                <div className="tp-actions">
                  <motion.button
                    className="tp-btn-primary"
                    onClick={() => setShowRegisterForm(true)}
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  >
                    <span>سجل مع المعلم</span>
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </motion.button>

                  <div className="tp-share-wrap">
                    <button className="tp-btn-share" onClick={() => setShowShareMenu(!showShareMenu)}>
                      <FontAwesomeIcon icon={faShare} />
                    </button>
                    <AnimatePresence>
                      {showShareMenu && (
                        <motion.div
                          className="tp-share-menu"
                          initial={{ opacity: 0, y: 10, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.9 }}
                        >
                          <button onClick={() => handleShare("facebook")}><FontAwesomeIcon icon={faFacebookF} /></button>
                          <button onClick={() => handleShare("whatsapp")}><FontAwesomeIcon icon={faWhatsapp} /></button>
                          <button onClick={() => handleShare("twitter")}><FontAwesomeIcon icon={faTwitter} /></button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            </aside>

            {/* Right Column: Dynamic Content */}
            <main className="tp-main">
              {/* About Section */}
              <motion.div className="tp-section" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <div className="tp-eyebrow"><span className="tp-eyebrow-dot" />نبذة تعريفية</div>
                <h2 className="tp-title">كلمة <em>المعلم</em></h2>
                <div className="tp-card-about">
                  <FontAwesomeIcon icon={faQuoteRight} className="tp-quote-icon" />
                  <p>{teacher.about || "معلم متميز يسعى دائماً لتقديم أفضل تجربة تعليمية للطلاب من خلال أساليب تدريس مبتكرة وفعالة تضمن الفهم الكامل للمحتوى."}</p>
                </div>
              </motion.div>

              {/* Stats Grid */}
              <motion.div className="tp-section" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <div className="tp-eyebrow">إحصائيات النجاح</div>
                <h2 className="tp-title">إنجازات <em>المعلم</em></h2>
                <div className="tp-stats-grid">
                  {stats.map((s, i) => (
                    <motion.div key={i} className="tp-stat-card" whileHover={{ y: -5 }}>
                      <div className="tp-stat-icon" style={{ color: s.color, background: `${s.color}10` }}>
                        <FontAwesomeIcon icon={s.icon} />
                      </div>
                      <div className="tp-stat-val">{s.value}</div>
                      <div className="tp-stat-lbl">{s.label}</div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Classes */}
              {teacher.teacher_classes?.length > 0 && (
                <motion.div className="tp-section" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                  <div className="tp-eyebrow">الصفوف الدراسية</div>
                  <h2 className="tp-title">المجموعات <em>المتاحة</em></h2>
                  <div className="tp-classes-wrap">
                    {teacher.teacher_classes.map((c, i) => (
                      <div key={i} className="tp-class-pill">
                        <FontAwesomeIcon icon={faUserGraduate} />
                        <span>{c}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Qualifications */}
              <motion.div className="tp-section" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <div className="tp-eyebrow">السيرة المهنية</div>
                <h2 className="tp-title">المؤهلات <em>العلمية</em></h2>
                <div className="tp-card-qual">
                  <div className="tp-qual-icon"><FontAwesomeIcon icon={faAward} /></div>
                  <div className="tp-qual-info">
                    <h4>{teacher.qualification}</h4>
                    <p>{teacher.specialization || "تخصص معتمد من وزارة التربية والتعليم"}</p>
                  </div>
                  <div className="tp-qual-badge">موثق</div>
                </div>
              </motion.div>
            </main>

          </div>
        </div>
      </section>

      <DynamicModal
        isOpen={showRegisterForm} onClose={() => setShowRegisterForm(false)}
        apiUrl="User/Add" formConfig={registerFormConfig(teacherId)}
        title="سجل مع معلمك الآن" submitButtonText="إتمام التسجيل"
        multipart={true} direction="rtl"
        onSubmit={async (formData) => {
          const fd = new FormData();
          Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
          const res = await ApiServices.post('User/Add', fd, true);
          if (!res.check) throw new Error(res.msg);
          toastHelper.success('تم التسجيل بنجاح');
          setShowRegisterForm(false);
        }}
        onError={(err) => toastHelper.error(err.message || 'حدث خطأ أثناء التسجيل')}
      />

      <Footer />

      <style jsx>{`
        .tp-root {
          font-family: "Zain", sans-serif;
          background: #fff;
          min-height: 100vh;
          direction: rtl;
        }

        .tp-container {
          max-width: 1300px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .tp-content { padding: 140px 0 100px; }

        .tp-grid {
          display: grid; grid-template-columns: 400px 1fr; gap: 64px; align-items: flex-start;
        }

        /* ── Profile Card ── */
        .tp-profile-card {
          background: #fff; border: 1.5px solid rgba(15,23,42,0.06);
          border-radius: 40px; padding: 48px; position: sticky; top: 140px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.03); text-align: center;
        }

        .tp-avatar-box { position: relative; width: 180px; height: 180px; margin: 0 auto 32px; }
        .tp-avatar-ring {
          position: absolute; inset: -8px; border-radius: 50%;
          border: 2px dashed #6366f1; opacity: 0.3; animation: tp-spin 20s linear infinite;
        }
        @keyframes tp-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .tp-avatar-img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; border: 6px solid #fff; box-shadow: 0 10px 30px rgba(0,0,0,0.1); }
        .tp-verified-badge {
          position: absolute; bottom: 10px; left: 10px; width: 40px; height: 40px;
          background: #6366f1; color: #fff; border-radius: 50%; border: 4px solid #fff;
          display: flex; align-items: center; justify-content: center; font-size: 1.1rem;
        }

        .tp-teacher-name { font-size: 2.2rem; font-weight: 900; color: #0f172a; margin: 0 0 8px; }
        .tp-qualification { font-size: 1.1rem; color: #64748b; font-weight: 700; display: flex; align-items: center; justify-content: center; gap: 8px; }
        .tp-qualification svg { color: #6366f1; }

        .tp-rating-box { background: #f8fafc; border-radius: 24px; padding: 24px; margin: 32px 0; }
        .tp-rating-head { display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 12px; }
        .tp-rating-val .num { font-size: 2.5rem; font-weight: 900; color: #6366f1; }
        .tp-rating-val .max { font-size: 1.1rem; color: #94a3b8; font-weight: 700; }
        .tp-rating-head .lbl { font-size: 0.95rem; color: #64748b; font-weight: 800; }
        .tp-stars { display: flex; justify-content: center; gap: 6px; }
        .tp-star-btn { border: none; background: #fff; width: 36px; height: 36px; border-radius: 10px; color: #e2e8f0; cursor: pointer; transition: 0.2s; }
        .tp-star-btn.is-active { color: #6366f1; background: #fffbeb; }

        .tp-quick-meta { display: flex; flex-direction: column; gap: 10px; margin-bottom: 32px; }
        .tp-meta-pill { display: flex; align-items: center; gap: 12px; padding: 12px 20px; background: #fcfcfd; border: 1px solid #f1f5f9; border-radius: 16px; font-weight: 800; color: #1e293b; font-size: 1.05rem; }
        .tp-meta-pill svg { color: #6366f1; width: 20px; }

        .tp-actions { display: flex; gap: 12px; }
        .tp-btn-primary {
          flex: 1; height: 56px; border: none; background: #6366f1; color: #fff;
          border-radius: 18px; font-family: inherit; font-weight: 900; font-size: 1.15rem;
          cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 12px;
          box-shadow: 0 10px 25px rgba(99, 102, 241, 0.2);
        }
        .tp-btn-share { width: 56px; height: 56px; border: 1.5px solid #f1f5f9; background: #fff; border-radius: 18px; color: #64748b; cursor: pointer; }
        .tp-share-wrap { position: relative; }
        .tp-share-menu {
          position: absolute; bottom: calc(100% + 15px); left: 0; background: #fff;
          border: 1.5px solid #f1f5f9; border-radius: 16px; padding: 10px;
          display: flex; gap: 10px; box-shadow: 0 15px 40px rgba(0,0,0,0.1); z-index: 100;
        }
        .tp-share-menu button { width: 40px; height: 40px; border: none; background: #f8fafc; border-radius: 10px; color: #6366f1; cursor: pointer; }

        /* ── Main Content ── */
        .tp-section { margin-bottom: 64px; }
        .tp-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          color: #6366f1; font-weight: 800; font-size: 0.85rem;
          letter-spacing: 0.14em; text-transform: uppercase; margin-bottom: 16px;
        }
        .tp-eyebrow-dot { width: 8px; height: 8px; border-radius: 50%; background: #6366f1; }
        .tp-title { font-size: 2.2rem; font-weight: 900; color: #0f172a; margin: 0 0 32px; }
        .tp-title em { font-style: normal; color: #6366f1; }

        .tp-card-about {
          background: #fff; border: 1.5px solid rgba(15,23,42,0.06); border-radius: 32px;
          padding: 48px; position: relative;
        }
        .tp-quote-icon { position: absolute; top: 20px; right: 20px; font-size: 3rem; color: #6366f1; opacity: 0.05; }
        .tp-card-about p { font-size: 1.3rem; color: #475569; line-height: 1.9; font-weight: 500; }

        .tp-stats-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; }
        .tp-stat-card {
          background: #fff; border: 1.5px solid rgba(15,23,42,0.06); border-radius: 28px;
          padding: 32px; text-align: center; transition: 0.3s;
        }
        .tp-stat-icon { width: 60px; height: 60px; border-radius: 18px; display: flex; align-items: center; justify-content: center; font-size: 1.6rem; margin: 0 auto 20px; }
        .tp-stat-val { font-size: 2.2rem; font-weight: 900; color: #0f172a; margin-bottom: 4px; }
        .tp-stat-lbl { font-size: 1.05rem; color: #94a3b8; font-weight: 800; }

        .tp-classes-wrap { display: flex; flex-wrap: wrap; gap: 12px; }
        .tp-class-pill {
          display: flex; align-items: center; gap: 10px; padding: 14px 24px;
          background: #fff; border: 1.5px solid #f1f5f9; border-radius: 50px;
          font-weight: 800; color: #1e293b; font-size: 1.1rem;
        }
        .tp-class-pill svg { color: #6366f1; }

        .tp-card-qual {
          display: flex; align-items: center; gap: 24px; background: #fff;
          border: 1.5px solid rgba(15,23,42,0.06); border-radius: 28px; padding: 32px;
        }
        .tp-qual-icon { width: 64px; height: 64px; background: #fefce8; color: #6366f1; border-radius: 16px; display: flex; align-items: center; justify-content: center; font-size: 2rem; }
        .tp-qual-info { flex: 1; }
        .tp-qual-info h4 { font-size: 1.4rem; font-weight: 900; color: #0f172a; margin: 0 0 4px; }
        .tp-qual-info p { font-size: 1.05rem; color: #64748b; font-weight: 700; }
        .tp-qual-badge { background: #ecfdf5; color: #10b981; padding: 4px 14px; border-radius: 50px; font-weight: 900; font-size: 0.85rem; }

        .tp-loader-box { height: 70vh; display: flex; align-items: center; justify-content: center; }

        @media (max-width: 1100px) {
          .tp-grid { grid-template-columns: 1fr; }
          .tp-aside { position: static; max-width: 100%; margin: 0 auto; }
          .tp-profile-card { position: static; }
        }

        @media (max-width: 768px) {
          .tp-content { padding-top: 120px; }
          .tp-stats-grid { grid-template-columns: 1fr; }
          .tp-card-about { padding: 24px 20px; }
          .tp-card-about p { font-size: 1.1rem; }
          .tp-teacher-name { font-size: 1.8rem; }
          .tp-title { font-size: 1.8rem; margin: 0 0 24px; }
          .tp-profile-card { padding: 24px; }
          .tp-avatar-box { width: 140px; height: 140px; margin-bottom: 24px; }
          .tp-card-qual { flex-direction: column; text-align: center; gap: 16px; padding: 24px; }
          .tp-qual-badge { margin-top: 8px; }
          .tp-quick-meta { flex-direction: column; }
          .tp-meta-pill { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default TeacherProfile;
